import {
    FundingOption
} from "@/shared/types";
import _ from "lodash";
import { defineStore } from "pinia";
import { useUserStore } from "@/shared/store/user";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { useHsaStore } from "@/shared/store/hsa";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { usePersonalContributionSchedulesStore } from "@/shared/store/personalContributionSchedules";
import { useContributionStore } from "@/shared/store/contribution";


export const useHsaContributionSummary = defineStore("hsaContributionSummary", {
    actions: {
        load (year: number, username?: string, partnerId?: number) {
            const hsaTotalStore = useHsaTotalStore();
            const hsaStore = useHsaStore();
            const hsaLimitStore = useHsaLimitStore();
            const contributionStore = useContributionStore();
            const personalContributionSchedulesStore = usePersonalContributionSchedulesStore();

            const customerServiceArguments = {
                username,
                partnerId
            };
            hsaStore.loadTransactionTotals({year, username, partnerId});
            hsaTotalStore.loadOtherHsaAmounts(customerServiceArguments);
            hsaTotalStore.getUserPayrollSummary(customerServiceArguments);
            hsaLimitStore.getHsaAccountLimits(customerServiceArguments);
            contributionStore.getPlanDetails(customerServiceArguments);
            if (this.showProjectedPersonalContributions) {
                // no username/partnerid args available here? that's a problem
                personalContributionSchedulesStore.getUpcomingPersonalContributionTotal(year);
            }


        }
    },
    getters: {
        isLoaded () {
            return (year: number) => {

                const hsaStore = useHsaStore();
                const personalContributionScheduleStore = usePersonalContributionSchedulesStore();


                return hsaStore.contributionTotalsLoaded(year)
                    && (this.showProjectedPersonalContributions ?
                 personalContributionScheduleStore.upcomingContributionsLoaded : true);
            };
        },

        hasBendProjectedPayroll (): boolean {
            const userStore = useUserStore();
            return userStore.employeeFundingOption === FundingOption.BendCalculatePull &&
                this.custodianSettingsStore.displayProjectedContributions;
        },

        showProjectedPersonalContributions () {
            const featureFlagStore = useFeatureFlagStore();
            const custodianSettingsStore = useCustodianSettingsStore();

            return featureFlagStore.flags.bend16612ContributionHandling && custodianSettingsStore.isHsaBankCustodian;
        },
        projectedPersonalContributions () {
            return (year: number): number => {
                const hsaStore = useHsaStore();
                const personalContributionScheduleStore = usePersonalContributionSchedulesStore();
                return hsaStore.totalEmployeePersonalContributions(year) +
                    (this.showProjectedPersonalContributions ? personalContributionScheduleStore.allUpcomingContributions : 0);
            };
        },
        annualEmployerContribution () {
            return (year: number) => {
                const hsaTotalStore = useHsaTotalStore();
                const hsaStore = useHsaStore();

                if (year > hsaTotalStore.activePayrollYear) {
                    return hsaTotalStore.nextYearEmployerTotal;
                }

                return hsaStore.totalEmployerPayrollContributions(year);
            };
        },
        projectedAnnualEmployerContribution () {
            return (year: number) => {
                const hsaTotalStore = useHsaTotalStore();

                if (!this.hasBendProjectedPayroll) {
                    return this.annualEmployerContribution(year);
                }

                if (year > hsaTotalStore.activePayrollYear) {
                    return hsaTotalStore.nextYearEmployerTotal;
                }

                return hsaTotalStore.employerAmount;
            };
        },
        projectedEmployeePayrollContributions () {
            return (year: number): number => {

                if (!this.hasBendProjectedPayroll) {
                    return this.employeePayrollContributions(year);
                }

                const result = this.projectedAnnualEmployeePreTaxPayrollContribution(year) +
                    this.employeePostTaxContributions(year);
                // adjust for rounding in floating point precision
                return _.round(result, 2);
            };
        },
        employeePostTaxContributions () {
            return (year: number) => {
                const hsaStore = useHsaStore();
                return hsaStore.employeePostTaxContributions(year);
            };
        },
        projectedAnnualEmployeePreTaxPayrollContribution () {
            return year => {
                const hsaTotalStore = useHsaTotalStore();
                if (year > hsaTotalStore.activePayrollYear) {
                    return hsaTotalStore.nextYearEmployeeTotal;
                }

                return hsaTotalStore.initialAnnualPreTaxContribution;
            };
        },
        employeePayrollContributions () {
            return year => {
                const result = this.annualEmployeePayrollContribution(year)
                    + this.employeePostTaxContributions(year);

                // adjust for rounding in floating point precision
                return _.round(result, 2);
            };
        },
        annualEmployeePayrollContribution () {
            return (year: number) => {
                const hsaStore = useHsaStore();
                const hsaTotalStore = useHsaTotalStore();

                if (year > this.activePayrollYear) {
                    return hsaTotalStore.nextYearEmployeeTotal;
                }

                return hsaStore.employeePreTaxContributions(year);
            };
        },
        annualEmployeePersonalContribution () {
            return (year: number) => {

                if (year > this.activePayrollYear) {
                    return 0;
                }
                const hsaStore = useHsaStore();
                return hsaStore.actualEmployeePersonalContributions(year);
            };
        },

        bankDirectContributions () {
            return (year: number) => {
                const hsaStore = useHsaStore();
                return hsaStore.bankDirectContributions(year);
            };
        },

        hsaLimit () {
            return (year: number) => {
                const hsaLimitStore = useHsaLimitStore();
                return hsaLimitStore.getHsaLimitByYear(year);
            };
        },

        otherHsaAmount () {
            return (year: number) => {
                const hsaTotalStore = useHsaTotalStore();
                return hsaTotalStore.otherHsaAmount(year);
            };
        },

        activePayrollYear () {
            const hsaTotalStore = useHsaTotalStore();
            return hsaTotalStore.activePayrollYear;
        },

        firstPlanEffectiveYear () {
            const contributionStore = useContributionStore();
            return contributionStore.firstPlanEffectiveYear;
        },

        remainingContributionAmount () {
            return (year: number) => {
                /*
                    console.log('hsaLimit(year)', this.hsaLimit(year));
                    console.log('projectedPersonalContributions(year)', this.projectedPersonalContributions(year));
                    console.log('projectedEmployeePayrollContributions(year)', this.projectedEmployeePayrollContributions(year));
                    console.log('projectedAnnualEmployerContribution(year)', this.projectedAnnualEmployerContribution(year));
                    console.log('otherHsaAmount(year)', this.otherHsaAmount(year));
                    console.log('bankDirectContributions(year)', this.bankDirectContributions(year));
                 */
                return Math.max(
                    this.hsaLimit(year) -
                    (this.projectedPersonalContributions(year) +
                    this.projectedEmployeePayrollContributions(year) +
                    this.projectedAnnualEmployerContribution(year) +
                    this.otherHsaAmount(year) +
                    this.bankDirectContributions(year)), 0);
            };
        }
    }
});